import { useEffect, useMemo, useState } from "react";
import {
  CreditCard,
  CalendarDays,
  Zap,
  Droplet,
  Send,
  Check,
  CheckCircle2,
  Circle,
  LogOut,
  Info,
  Layers,
  Flame,
} from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { GenericSelect } from "@shared/components/Select";
import { TableColumn } from "@shared/types/table";
import { Switch } from "@shared/ui/switch";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useMeterReadings } from "@features/cx/disconnect/hooks";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useApproveSingleBill, useBillDetails, useBillTemplates, useConsumerBill, useGenerateBill, useMeterReadingStatus, usePaymentForBill, usePrintBill, useUpdateTransferRequest } from "@features/cx/transfer/hooks";
import { Button } from "@shared/ui/button";
import { useDateNormalization } from "@shared/hooks/useDateNormalization";
import { SERVICE_REQUEST_STATUS } from "@features/cx/shared/utils/constants";
import { toast } from "@shared/hooks/use-toast";

  interface MeterReading {
    id: number;
    productCode?: string;
    meterNumber?: string;
    deviceNo?: string;
    currentReadingMeterStatus?: string | number;
    currentReading?: string | number;
    currentReadingDate?: string;
    [key: string]: any; // To accommodate any extra properties from API
  }


// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------
const SectionHeader = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) => (
  <div>
    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
  </div>
);

const StatusCard = ({
  active,
  tone,
  icon,
  title,
  value,
}: {
  active: boolean;
  tone: "success" | "warning" | "neutral";
  icon: React.ReactNode;
  title: string;
  value: string;
}) => {
  const toneClass =
    tone === "success"
      ? "border-green-300 bg-green-50"
      : tone === "warning"
      ? "border-gray-200 bg-white"
      : "border-gray-200 bg-white";

  const valueColor =
    tone === "success"
      ? "text-green-700"
      : tone === "warning"
      ? "text-orange-500"
      : "text-gray-400";

  const iconColor =
    tone === "success"
      ? "text-green-600"
      : tone === "warning"
      ? "text-orange-500"
      : "text-gray-400";

  return (
    <div
      className={`rounded-lg border-2 px-4 py-3 flex items-center gap-3 ${
        active ? toneClass : "border-gray-200 bg-white"
      }`}
    >
      <span className={iconColor}>{icon}</span>
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className={`text-sm font-bold ${valueColor}`}>{value}</p>
      </div>
    </div>
  );
};

// --- New Reusable Sub-components ---

const AdjustmentSection = ({
  icon: Icon,
  title,
  label,
  value,
  checked,
  onChange,
  accent,
  disabled,
}: {
  icon: any;
  title: string;
  label: string;
  value: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  accent: "blue" | "purple";
  disabled?: boolean;
}) => {
  const isBlue = accent === "blue";
  const bgClass = isBlue ? "bg-blue-50 border-blue-500" : "bg-purple-50 border-purple-500";
  const textClass = isBlue ? "text-blue-600" : "text-purple-600";
  const amountClass = isBlue ? "text-blue-700" : "text-purple-700";

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6 space-y-4 shadow-sm">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-gray-700" />
        <h3 className="font-bold text-gray-900">{title}</h3>
      </div>
      <div className={`flex items-center justify-between border-l-4 rounded-md px-4 py-3 ${bgClass}`}>
        <div>
          <p className={`text-sm font-semibold ${textClass}`}>{label}</p>
          <p className={`text-2xl font-extrabold mt-1 ${amountClass}`}>{value}</p>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            Include in Final Bill <Info className="w-3 h-3" />
          </p>
        </div>
        <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
      </div>
    </section>
  );
};

const MeterSection = ({
  icon: Icon,
  title,
  data,
  columns,
}: {
  icon: any;
  title: string;
  data: any[];
  columns: TableColumn<any>[];
}) => (
  <div className="rounded-lg border border-gray-200 overflow-hidden shadow-sm">
    <div className="flex items-center gap-2 bg-gray-50 px-4 py-3">
      <Icon className="w-4 h-4 text-blue-600" />
      <span className="font-semibold text-blue-600">{title}</span>
    </div>
    <GenericTable data={data} rowKey="id" columns={columns} />
  </div>
);

const PRODUCT_CONFIG: Record<string, { icon: any; }> = {
  Electricity: { icon: Zap },
  Water: { icon: Droplet },
  Gas: { icon: Flame },
  Default: { icon: Layers },
};


export const BillPaymentsTab = ({
  consumerId,
  requestId,
  premise,
  additionalData,
  requestStatus
}: {
  consumerId: number;
  requestId: number;
  premise: string;
  additionalData: any;
  requestStatus: string;
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: billTemplates } = useBillTemplates(remoteUtilityId);
  const { normalizeWithUserFormat } = useDateNormalization();
  const {data:paymentForBill} = usePaymentForBill(String(consumerId),additionalData?.billId)
  console.log('payment',paymentForBill,additionalData)
  const isApproved=additionalData?.billStatus?.toLowerCase() === "approved";
  const invoiceFields: FormField[] = [
    {
      name: "startDate",
      label: "Start Date",
      type: "datepicker",
      required: true,
      disabled:isApproved
    },
    {
      name: "endDate",
      label: "End Date",
      type: "datepicker",
      required: true,
      disabled:isApproved
    },
    {
      name: "template",
      label: "Select Template",
      type: "select",
      options:
          billTemplates?.map((item) => ({
            label: item.configName,
            value: item.id,
          })) ?? [],
      required: true,
      disabled:isApproved
    },
  ];

  // Invoice Form
  const invoiceForm = FormService.createForm({
    fields: invoiceFields,
  });

   useEffect(() => {
    if (additionalData?.billId) {
      setBill({
        billId: additionalData.billId,
        billNumber: additionalData.billNumber,
        billAmount: additionalData.billAmount,
        billStatus: additionalData.billStatus,
        dueDate: additionalData.dueDate,
        totalOutstanding: additionalData?.totalOutstanding,
        invoicePeriod:additionalData?.invoicePeriod,
        balanceCarriedForward:additionalData?.balanceCarriedForward //GS is providing this key
      });

      FormService.populateForm(invoiceForm,{
        startDate: normalizeWithUserFormat(additionalData?.invoiceStartDate),
        endDate: normalizeWithUserFormat(additionalData?.invoiceEndDate),
        template: additionalData?.billConfigId
      },invoiceFields)


      setCreditApplied(additionalData?.includeCredit)
    }
  }, [additionalData]);

   useEffect(() => {
    console.log("request status:",requestStatus?.toLowerCase())
    if (requestStatus?.toLowerCase() === "completed" || requestStatus?.toLowerCase() === "rejected" ) {
      setTransferClosed(true);
    }
  }, [requestStatus]);


  const formValues = invoiceForm.watch();
  const currency = useCurrency();
  const generateBillMutation = useGenerateBill();
  const { data: meterReadingStatus } = useMeterReadingStatus();
  const { mutateAsync: updateServiceRequest, isPending: isUpdatingTransfer } = useUpdateTransferRequest(requestId);
  
  const { data: consumerBill } = useConsumerBill({
    consumerId: consumerId,
    remoteUtilityId: remoteUtilityId,
  });

  const rawBillData = consumerBill?.result?.billData;

  const billData = Array.isArray(rawBillData) ? rawBillData[0] : rawBillData;

  const { data: meterReadings } = useMeterReadings({
    remoteUtilityId: Number(remoteUtilityId),
    meterId: consumerBill?.result?.meters,
    startDate: formValues.startDate,
    endDate: formValues.endDate,
  });

  const [readings, setReadings] = useState<MeterReading[]>([]);

  useEffect(() => {
    console.log("meterReadings",meterReadings,additionalData?.readingData);
    if(additionalData?.readingData){
      const mappedReadings = additionalData.readingData.map((r: any) => ({
        id: r.meter,
        productCode: r.productCode,
        meterNumber: r.meterNumber,
        deviceNo: r.deviceNo,
        currentReading: r.meterReading,
        currentReadingDate: normalizeWithUserFormat(r.readingDate),
        currentReadingMeterStatus: r.readingSource,
      }));
      setReadings(mappedReadings);
      return;
    }
    if (Array.isArray(meterReadings)) {
      setReadings(meterReadings);
    }
  }, [meterReadings,additionalData]);

  const updateReading = (id: number, field: string, value: any) => {
    setReadings((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const groupedReadings = useMemo(() => {
    return readings.reduce((acc: Record<string, any[]>, reading) => {
      const code = reading.productCode || "Other";
      if (!acc[code]) acc[code] = [];
      acc[code].push(reading);
      return acc;
    }, {});
  }, [readings]);

  // Adjustments
  const [creditApplied, setCreditApplied] = useState(false);
  const [paymentAgreementApplied, setPaymentAgreementApplied] = useState(false);

  // Bill + closure
  const [bill, setBill] = useState<any | null>(null);
  const [paymentReceived, setPaymentReceived] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [transferClosed, setTransferClosed] = useState(false);

  // Derived closure status
  const billGenerated = bill !== null;
  const paymentStatus: "Received" | "Pending" = paymentReceived
    ? "Received"
    : "Pending";
  const transferStatus: "Closed" | "Not Closed" = transferClosed
    ? "Closed"
    : "Not Closed";

  const { mutateAsync: approveBill, isPending: isApprovingBill } =
        useApproveSingleBill(requestId);
  
  const printBill = usePrintBill();

  useEffect(() => {
    if (paymentForBill?.statusDisplay?.toLowerCase()==="debit") {
      setPaymentReceived(true)
    }
  }, [paymentForBill]);
  

  const handleGenerateBill = async() => {
     const isValid = await FormService.validateForm(invoiceForm);
        if (!isValid) {
          return;
        }
    if (!consumerBill?.result) return;

    const consumerData = consumerBill.result;

    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      start_date: formValues.startDate,
      end_date: formValues.endDate,
      premise: premise || "",
      bill_config_id: formValues.template,
      bill_config_type: "new",
      request_id: requestId,
      consumer_data: {
        id: consumerData.id,
        consumer_no: consumerData.consumerNo,
        status: consumerData.status,
        is_e_bill: consumerData.isEBill ?? true,
        is_papered_bill: consumerData.isPaperedBill ?? false,
      },
      bill_data: {
        remote_consumer_number: consumerData.consumerNo,
        remote_utility_id: Number(remoteUtilityId),
        consumer_data: {
          id: consumerData.id,
          consumer_no: consumerData.consumerNo,
          status: consumerData.status,
          is_e_bill: consumerData.isEBill ?? true,
          is_papered_bill: consumerData.isPaperedBill ?? false,
        },
        invoice_start_date: formValues.startDate,
        invoice_end_date: formValues.endDate,
        bill_config_id: formValues.template,
        bill_config_type: "new",
        include_service_charge: true,
        include_credit: creditApplied,
        include_payment_agreement: paymentAgreementApplied,
      },
      reading_data: readings.map((r) => ({
        meter: r.id,
        meter_number: r.meterNumber,
        product_code: r.productCode,
        meter_reading: Number(r.currentReading),
        reading_source: 1,
        reading_date: r.currentReadingDate,
        status: r.currentReadingMeterStatus,
      })),
      bill_generate: "true",
      step: {
        step_working_on: 2,
        total_steps: 3,
      },
    };

    generateBillMutation.mutate(payload, {
      onSuccess: (data) => {
        setBill(data);
      },
    });
  };

  const handleCloseTransfer = async () => {
    try {
      await updateServiceRequest({
        status: SERVICE_REQUEST_STATUS.COMPLETED,
        closeDate: new Date().toISOString(),
      });
      setTransferClosed(true);
    } catch (error) {
      console.error("Failed to close transfer:", error);
    }
  };

  const handleFinalApproval = async () => {
    
    try {
      const payload = {
        sendCommunication: true,
      };
      const response = await approveBill({ billId: additionalData?.billId || bill?.billId, payload });
      // setBillApproved(true);
    } catch (error) {
      console.error("Error approving bill:", error);
    }
  };

   const handlePrintClick = async (billId: string) => {
    try {
      printBill.mutateAsync(billId);
    } catch (error) {}
  };

  const createMeterColumns = (): TableColumn<any>[] => [
    { key: "meterNumber", header: "METER NUMBER" },
    { key: "deviceNo", header: "DEVICE NUMBER" },
    {
      key: "currentReadingMeterStatus",
      header: "READING STATUS",
      render: (value, row) => (
        <GenericSelect
          value={value}
          onChange={(val) =>
            updateReading(row.id, "currentReadingMeterStatus", val)
          }
          options={meterReadingStatus?.map((item) => ({
            label: item.value,
            value: item.key,
          })) || []}
          placeholder="Select"
          className="h-9 min-w-[140px]"
          isSearchable={false}
          disabled={!!additionalData?.billId}
        />
      ),
    },
    {
      key: "currentReading",
      header: `CURRENT READING`,
      render: (value, row) => (
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => updateReading(row.id, "currentReading", e.target.value)}
          disabled={!!additionalData?.billId}
          placeholder="Enter"
          className="border border-gray-300 rounded-md px-2 py-1 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
        />
      ),
    },
    {
      key: "currentReadingDate",
      header: "READING DATE",
      render: (value, row) => (
        <input
          type="date"
          value={value || ""}
          onChange={(e) =>
            updateReading(row.id, "currentReadingDate", e.target.value)
          }
          disabled={!!additionalData?.billId}
          className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
        />
      ),
    },
  ];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6 = min-h-screen">
      {/* =================================================================== */}
      {/* Invoice Details                                                     */}
      {/* =================================================================== */}
      <section className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
        <SectionHeader
          title="Invoice Details"
          subtitle="Configure the billing period and invoice template for the final bill."
        />

        <DynamicForm
          form={invoiceForm}
          fields={invoiceFields}
          gridColumns={3}
          className="p-0 grid-cols-1 md:grid-cols-1"
          config={{ showSubmitButton: false }}
        />
      </section>

      {/* =================================================================== */}
      {/* Credit + Payment Agreement                                          */}
      {/* =================================================================== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AdjustmentSection
          icon={CreditCard}
          title="Credit Amount Adjustment"
          label="Available Credit Amount"
          value={`${currency} ${consumerBill?.result?.totalCreditAmount ?? 'NA'}`}
          checked={creditApplied}
          onChange={setCreditApplied}
          accent="blue"
          disabled={!!additionalData?.billId}
        />
        <AdjustmentSection
          icon={CalendarDays}
          title="Payment Agreement Active"
          label="Total Remaining Value"
          value={`${currency} ${consumerBill?.result?.remainingAgreementAmount ?? 'NA'}`}
          checked={paymentAgreementApplied}
          onChange={setPaymentAgreementApplied}
          accent="purple"
          disabled={!!additionalData?.billId}
        />
      </div>

      {/* =================================================================== */}
      {/* Meter Details                                                       */}
      {/* =================================================================== */}
      <section className="rounded-xl border border-gray-200 bg-white p-6 space-y-5">
        <SectionHeader
          title="Meter Details"
          subtitle="Confirm final meter metadata and reading dates before bill generation."
        />

        {
          Object.keys(groupedReadings).length === 0 && (
            <p className="text-center text-gray-500">No meter readings found!</p>
          )
        }

        {
          !formValues.startDate && !formValues.endDate && (
            <p className="text-center text-gray-500">Select start date and end date to get the meter readings</p>
          )
        }

        {Object.entries(groupedReadings).map(([productCode, readings]) => {
          const config = PRODUCT_CONFIG[productCode] || PRODUCT_CONFIG.Default;
          return (
            <MeterSection
              key={productCode}
              icon={config.icon}
              title={`${productCode} Meter`}
              data={readings}
              columns={createMeterColumns()}
            />
          );
        })}

        {/* Generate bill button */}
        <div>
          <Button
            type="button"
            onClick={handleGenerateBill}
            disabled={isApproved || !formValues.startDate || !formValues.endDate}
            isLoading={generateBillMutation.isPending}
          >
            {isApproved ? "Final Bill Generated" : "Generate Final Bill"}
          </Button>
        </div>
      </section>

      {/* =================================================================== */}
      {/* Final Bill Summary                                                  */}
      {/* =================================================================== */}
      {bill && (
        <section className="space-y-4">
          <h2 className="text-lg font-bold text-red-600">Final Bill Summary</h2>

          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            {/* Invoice info */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-900">Invoice Information</h3>
              <div className="flex flex-wrap gap-x-8 gap-y-1 mt-1 text-sm">
                <span className="text-gray-500">
                  Invoice No:{" "}
                  <span className="text-gray-900 font-semibold">
                    {bill.billNumber}
                  </span>
                </span>
                <span className="text-gray-500">
                  Billing Period:{" "}
                  <span className="text-gray-900 font-semibold">
                    {bill.invoicePeriod}
                  </span>
                </span>
              </div>
            </div>

            {/* Bill details */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-900 mb-3">Bill Details</h3>
              <div className="flex justify-between text-sm py-1">
                <span className="text-green-600 font-medium">Billed Amount</span>
                <span className="text-gray-900 font-semibold">
                  {currency} {bill.billAmount}
                </span>
              </div>
              <div className="flex justify-between text-sm py-1">
                <span className="text-red-600 font-medium">
                  Outstanding Amount
                </span>
                <span className="text-gray-900 font-semibold">
                  {currency} {bill?.balanceCarriedForward ?? 'NA'}
                </span>
              </div>
            </div>

            {/* Final amount due */}
            <div className="flex justify-between items-center px-6 py-4 border-l-4 border-red-500 bg-white border-b border-gray-200">
              <span className="font-bold text-red-600">Final Amount Due</span>
              <span className="font-bold text-blue-600 text-lg">
                {currency} {bill.billAmount + (bill?.balanceCarriedForward || 0)}
              </span>
            </div>            
          </div>

          {/* Print + Approve */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={() => handlePrintClick(additionalData?.billId || bill?.billId)}
              variant="success"
            >
              <Check className="w-4 h-4" />
              Print Bill
            </Button>
            <Button
              onClick={() =>handleFinalApproval()}
              disabled={isApproved}       
            >
              <Send className="w-4 h-4" />
              { isApproved ? "Approved" : "Approve"}
            </Button>
          </div>
        </section>
      )}

      {/* =================================================================== */}
      {/* Transfer Request Closure                                            */}
      {/* =================================================================== */}
      {bill && (
        <section className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-200">
            <div className="bg-red-50 p-2.5 rounded-lg flex items-center justify-center">
              <LogOut className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Transfer Request Closure</h2>
              <p className="text-sm text-gray-500">
                Complete the transfer by providing closure details and final
                remarks
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Closure status */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-bold text-gray-900 mb-3">Closure Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <StatusCard
                  active={billGenerated}
                  tone="success"
                  icon={
                    billGenerated ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Circle className="w-5 h-5" />
                    )
                  }
                  title="Final Bill"
                  value={billGenerated ? "Generated" : "Pending"}
                />
                <StatusCard
                  active={paymentReceived}
                  tone={paymentReceived ? "success" : "warning"}
                  icon={
                    paymentReceived ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Circle className="w-5 h-5" />
                    )
                  }
                  title="Payment"
                  value={paymentStatus}
                />
                <StatusCard
                  active={transferClosed}
                  tone={transferClosed ? "success" : "neutral"}
                  icon={
                    transferClosed ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Circle className="w-5 h-5" />
                    )
                  }
                  title="Transfer"
                  value={transferStatus}
                />
              </div>
            </div>

            {/* Bill info table */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Bill Information</h3>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-5 gap-4 px-4 py-2 text-xs font-semibold text-gray-500 tracking-wider bg-gray-50">
                  {[
                    "INVOICE NO",
                    "BILL STATUS",
                    "PAYMENT STATUS",
                    "TOTAL AMOUNT",
                    "PENDING AMOUNT",
                  ].map((header) => (
                    <span key={header}>{header}</span>
                  ))}
                </div>
                <div className="grid grid-cols-5 gap-4 px-4 py-4 items-center text-sm border-t border-gray-200">
                  {[
                    <div
                      key="invoice-no"
                      className="text-blue-600 font-semibold"
                    >
                      {bill.billNumber}
                    </div>,
                    <span key="bill-status">
                      <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                       {bill.billStatus}
                      </span>
                    </span>,
                    <span key="payment-status">
                      <span
                        className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${
                          paymentReceived
                            ? "bg-green-100 text-green-700"
                            : "bg-orange-100 text-orange-700"
                        }`}
                      >
                        {paymentStatus}
                      </span>
                    </span>,
                    <span key="total-amount" className="font-semibold text-gray-900">
                      {currency} {bill.billAmount}
                    </span>,
                    <span
                      key="pending-amount"
                      className={`font-semibold ${
                        paymentReceived ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {currency}{" "}
                      {billData?.outstandingBalance}
                    </span>,
                  ].map((content, idx) => (
                    <span key={idx}>{content}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Payment info table (shown once payment received) */}
            {paymentReceived && (
              <div>
                <h3 className="font-bold text-gray-900 mb-3">
                  Payment Information
                </h3>
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <div className="grid grid-cols-3 gap-4 px-4 py-2 text-xs font-semibold text-gray-500 tracking-wider bg-gray-50">
                    {["RECEIPT NO", "PAYMENT DATE", "PAYMENT AMOUNT"].map(
                      (header) => (
                        <span key={header}>{header}</span>
                      ),
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4 px-4 py-4 items-center text-sm border-t border-gray-200">
                    {[
                      <div
                        key="receipt-no"
                        className="text-blue-600 font-semibold"
                      >
                        {bill.billNumber}
                      </div>,
                      <span key="payment-date" className="text-gray-900">
                        {paymentForBill?.createdDate}
                      </span>,
                      <span
                        key="payment-amount"
                        className="font-semibold text-green-600"
                      >
                        {currency} {paymentForBill?.amount}
                      </span>,
                    ].map((content, idx) => (
                      <span key={idx}>{content}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Closure details */}
            <div className="space-y-4">
              <h3 className="font-bold text-gray-900">Closure Details</h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Remarks
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  disabled={transferClosed}
                  rows={4}
                  placeholder="Add any additional remarks or notes for this closure..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 resize-y"
                />
              </div>
            </div>

            {/* Footer action */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500 flex items-center gap-1.5">
                <Info className="w-4 h-4" />
                {transferClosed
                  ? "This transfer request has been permanently closed."
                  : "This action will permanently close the transfer request."}
              </p>
              <Button
                onClick={handleCloseTransfer}
                disabled={transferClosed || isUpdatingTransfer}
                isLoading={isUpdatingTransfer}
                variant="destructive"
              >
                <LogOut className="w-4 h-4" />
                {transferClosed ? "Transfer Closed" : "Close Transfer Request"}
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

